import { useState, useEffect } from 'react';
import {
  ResponsiveModal,
  ResponsiveModalContent,
  ResponsiveModalHeader,
  ResponsiveModalBody,
  ResponsiveModalFooter,
  ResponsiveModalTitle,
  ResponsiveModalDescription,
} from '@/components/ui/responsive-modal';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Loader2, Shield, KeyRound, AlertTriangle } from 'lucide-react';
import { useTeam, type TeamMember, type UpdateStaffInput } from '@/hooks/useTeam';
import { useTwoFactor } from '@/hooks/useTwoFactor';
import { useLocations } from '@/hooks/useLocations';
import { PermissionEditor } from './PermissionEditor';
import { LocationPermissionEditor } from './LocationPermissionEditor';
import { useAuth } from '@/hooks/useAuth';
import { usePermissions } from '@/hooks/usePermissions';
import { useOwner2FAApproval } from '@/hooks/useOwner2FAApproval';

interface EditStaffDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  staff: TeamMember | null;
  onSuccess?: () => void;
}

type TabType = 'details' | 'locations' | 'permissions';

export function EditStaffDialog({ open, onOpenChange, staff, onSuccess }: EditStaffDialogProps) {
  const { updateStaff, updateLocationAccess, deactivateStaff, reactivateStaff } = useTeam();
  const { locations } = useLocations();
  const { profile } = useAuth();
  const { getUserLocationPermissions } = usePermissions();
  const { requireApproval, owner2FAEnabled } = useOwner2FAApproval();
  const { adminResetUser2FA } = useTwoFactor();

  const [activeTab, setActiveTab] = useState<TabType>('details');
  const [formData, setFormData] = useState<UpdateStaffInput>({});
  const [selectedLocationIds, setSelectedLocationIds] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [showReset2FAConfirm, setShowReset2FAConfirm] = useState(false);

  // Location permission editor state
  const [locationPermEditorOpen, setLocationPermEditorOpen] = useState(false);
  const [selectedLocationForPerms, setSelectedLocationForPerms] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [locationOverrideCounts, setLocationOverrideCounts] = useState<Record<string, number>>({});

  // Load location permission override counts
  const loadLocationOverrideCounts = async (userId: string) => {
    try {
      const locPerms = await getUserLocationPermissions(userId);
      const counts: Record<string, number> = {};
      for (const lp of locPerms) {
        if (lp.permissions && typeof lp.permissions === 'object') {
          counts[lp.location_id] = Object.keys(lp.permissions).length;
        }
      }
      setLocationOverrideCounts(counts);
    } catch (error) {
      console.error('Error loading location override counts:', error);
    }
  };

  // Reset form when staff changes
  useEffect(() => {
    if (staff) {
      setFormData({
        first_name: staff.first_name || '',
        last_name: staff.last_name || '',
        role: staff.role as UpdateStaffInput['role'],
        is_active: staff.is_active,
      });
      setSelectedLocationIds(
        staff.location_access?.map(la => la.location_id) || []
      );
      setActiveTab('details');
      setError(null);
      setLocationOverrideCounts({});
      loadLocationOverrideCounts(staff.id);
    }
  }, [staff]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!staff) return;
    setError(null);

    try {
      // Check if role is being changed and requires owner 2FA approval
      const roleChanged = formData.role && formData.role !== staff.role;
      const isPromotingToAdmin = roleChanged && ['admin', 'owner', 'primary_admin'].includes(formData.role!);

      if (owner2FAEnabled && isPromotingToAdmin) {
        const approved = await requireApproval({
          actionType: 'role_change',
          metadata: {
            userId: staff.id,
            userName: `${staff.first_name} ${staff.last_name}`,
            fromRole: staff.role,
            toRole: formData.role,
          },
          description: `Change role for ${staff.first_name} ${staff.last_name} from ${staff.role} to ${formData.role}`,
        });

        if (!approved) {
          setError('Owner 2FA approval denied for role change');
          return;
        }
      }

      await updateStaff.mutateAsync({ id: staff.id, updates: formData });

      // Update location access if changed
      const currentLocationIds = staff.location_access?.map(la => la.location_id) || [];
      const locationsChanged =
        selectedLocationIds.length !== currentLocationIds.length ||
        !selectedLocationIds.every(id => currentLocationIds.includes(id));

      if (locationsChanged) {
        await updateLocationAccess.mutateAsync({
          userId: staff.id,
          locationIds: selectedLocationIds,
        });
      }

      onOpenChange(false);
      onSuccess?.();
    } catch (err: any) {
      setError(err.message || 'Failed to update staff member');
    }
  };

  const handleToggleActive = async () => {
    if (!staff) return;

    try {
      // Check if deactivating an admin+ user requires owner 2FA approval
      const isDeactivatingAdminPlus = staff.is_active && ['admin', 'owner', 'primary_admin'].includes(staff.role);

      if (owner2FAEnabled && isDeactivatingAdminPlus) {
        const approved = await requireApproval({
          actionType: 'user_deactivate',
          metadata: {
            userId: staff.id,
            userName: `${staff.first_name} ${staff.last_name}`,
            role: staff.role,
          },
          description: `Deactivate ${staff.role} account: ${staff.first_name} ${staff.last_name}`,
        });

        if (!approved) {
          setError('Owner 2FA approval denied for deactivation');
          return;
        }
      }

      if (staff.is_active) {
        await deactivateStaff.mutateAsync(staff.id);
      } else {
        await reactivateStaff.mutateAsync(staff.id);
      }
      onSuccess?.();
    } catch (err: any) {
      setError(err.message || 'Failed to update status');
    }
  };

  const handleLocationToggle = (locationId: string) => {
    setSelectedLocationIds(prev =>
      prev.includes(locationId)
        ? prev.filter(id => id !== locationId)
        : [...prev, locationId]
    );
  };

  // Check if current user can edit this staff member
  const canEdit = () => {
    if (!profile || !staff) return false;
    if (profile.role === 'superadmin') return true;
    // Business owners can edit other business owners (but not superadmin)
    if (profile.role === 'primary_admin') return staff.role !== 'superadmin';
    if (profile.role === 'owner') return staff.role !== 'owner' && staff.role !== 'primary_admin' && staff.role !== 'superadmin';
    if (profile.role === 'admin') return staff.role !== 'owner' && staff.role !== 'admin' && staff.role !== 'primary_admin' && staff.role !== 'superadmin';
    return false;
  };

  // Check if current user can reset 2FA for this staff member
  const canReset2FA = () => {
    if (!profile || !staff) return false;
    // Only primary_admin and owner can reset 2FA for others
    if (!['primary_admin', 'owner', 'superadmin'].includes(profile.role)) return false;
    // Can't reset your own 2FA this way
    if (staff.id === profile.id) return false;
    return canEdit();
  };

  const handleReset2FA = async () => {
    if (!staff) return;

    try {
      await adminResetUser2FA.mutateAsync(staff.id);
      setShowReset2FAConfirm(false);
      onSuccess?.();
    } catch (err: any) {
      setError(err.message || 'Failed to reset 2FA');
    }
  };

  if (!staff) return null;

  const tabs: { id: TabType; label: string }[] = [
    { id: 'details', label: 'Details' },
    { id: 'locations', label: 'Locations' },
    { id: 'permissions', label: 'Permissions' },
  ];

  return (
    <ResponsiveModal open={open} onOpenChange={onOpenChange}>
      <ResponsiveModalContent maxWidth="lg">
        <ResponsiveModalHeader>
          <ResponsiveModalTitle>
            Edit Staff Member
          </ResponsiveModalTitle>
          <ResponsiveModalDescription>
            {staff.first_name} {staff.last_name} • {staff.role}
          </ResponsiveModalDescription>
        </ResponsiveModalHeader>

        {/* Tabs */}
        <div className="flex border-b px-6 mt-2">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col flex-1 overflow-hidden">
          <ResponsiveModalBody>
            <div className="min-h-[280px]">
              {error && (
                <div className="p-3 mb-4 text-sm text-red-600 bg-red-50 dark:bg-red-950/20 rounded-lg">
                  {error}
                </div>
              )}

              {/* Details Tab */}
              {activeTab === 'details' && (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="edit_first_name">First Name</Label>
                      <Input
                        id="edit_first_name"
                        value={formData.first_name || ''}
                        onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                        disabled={!canEdit()}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="edit_last_name">Last Name</Label>
                      <Input
                        id="edit_last_name"
                        value={formData.last_name || ''}
                        onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                        disabled={!canEdit()}
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="edit_role">Role</Label>
                    <Select
                      value={formData.role}
                      onValueChange={(value) =>
                        setFormData({ ...formData, role: value as UpdateStaffInput['role'] })
                      }
                      disabled={!canEdit()}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="employee">Employee</SelectItem>
                        <SelectItem value="manager">Manager</SelectItem>
                        <SelectItem value="admin">Admin</SelectItem>
                        {(profile?.role === 'superadmin' || profile?.role === 'primary_admin') && (
                          <>
                            <SelectItem value="owner">Owner</SelectItem>
                            <SelectItem value="primary_admin">Business Owner</SelectItem>
                          </>
                        )}
                      </SelectContent>
                    </Select>
                    {staff.role === 'primary_admin' && (
                      <p className="text-xs text-muted-foreground">
                        Business Owner has full access to all business features
                      </p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="edit_pin">Reset PIN</Label>
                    <Input
                      id="edit_pin"
                      type="password"
                      maxLength={4}
                      value={formData.pin || ''}
                      onChange={(e) => setFormData({ ...formData, pin: e.target.value.replace(/\D/g, '') })}
                      placeholder="Leave empty to keep current"
                      disabled={!canEdit()}
                    />
                  </div>

                  <div className="flex items-center justify-between p-4 border rounded-lg">
                    <div>
                      <Label>Account Status</Label>
                      <p className="text-sm text-muted-foreground">
                        {staff.is_active ? 'Active - can log in and use the system' : 'Deactivated - cannot log in'}
                      </p>
                    </div>
                    <Switch
                      checked={staff.is_active}
                      onCheckedChange={handleToggleActive}
                      disabled={!canEdit() || staff.id === profile?.id}
                    />
                  </div>

                  {/* Reset 2FA - Only for primary_admin/owner */}
                  {canReset2FA() && (
                    <div className="p-4 border rounded-lg space-y-3">
                      <div className="flex items-center gap-2">
                        <KeyRound className="h-4 w-4 text-muted-foreground" />
                        <Label>Two-Factor Authentication</Label>
                      </div>
                      {!showReset2FAConfirm ? (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => setShowReset2FAConfirm(true)}
                          className="text-amber-600 border-amber-200 hover:bg-amber-50 dark:border-amber-800 dark:hover:bg-amber-950/30"
                        >
                          <KeyRound className="h-4 w-4 mr-2" />
                          Reset 2FA
                        </Button>
                      ) : (
                        <div className="p-3 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-lg space-y-3">
                          <div className="flex items-start gap-2">
                            <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
                            <p className="text-sm text-amber-700 dark:text-amber-300">
                              This will disable 2FA for {staff.first_name} {staff.last_name}. They will need to set up 2FA again.
                            </p>
                          </div>
                          <div className="flex gap-2">
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => setShowReset2FAConfirm(false)}
                            >
                              Cancel
                            </Button>
                            <Button
                              type="button"
                              variant="destructive"
                              size="sm"
                              onClick={handleReset2FA}
                              disabled={adminResetUser2FA.isPending}
                            >
                              {adminResetUser2FA.isPending && (
                                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                              )}
                              Confirm Reset
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Locations Tab */}
              {activeTab === 'locations' && (
                <div className="space-y-4">
                  <p className="text-sm text-muted-foreground">
                    Select which locations this staff member can access. You can also set location-specific permission overrides.
                  </p>
                  <div className="border rounded-lg divide-y">
                    {locations.map((location) => {
                      const isSelected = selectedLocationIds.includes(location.id);
                      const overrideCount = locationOverrideCounts[location.id] || 0;

                      return (
                        <div
                          key={location.id}
                          className="flex items-center justify-between p-3 hover:bg-muted/50"
                        >
                          <label className="flex items-center gap-3 cursor-pointer flex-1">
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => handleLocationToggle(location.id)}
                              disabled={!canEdit()}
                              className="rounded border-input"
                            />
                            <span className="font-medium">{location.name}</span>
                          </label>
                          {isSelected && canEdit() && (
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="gap-1.5 text-muted-foreground hover:text-foreground"
                              onClick={() => {
                                setSelectedLocationForPerms({ id: location.id, name: location.name });
                                setLocationPermEditorOpen(true);
                              }}
                            >
                              <Shield className="h-3.5 w-3.5" />
                              Permissions
                              {overrideCount > 0 && (
                                <Badge variant="secondary" className="ml-1 h-5 px-1.5 text-xs bg-amber-500/20 text-amber-600">
                                  {overrideCount}
                                </Badge>
                              )}
                            </Button>
                          )}
                        </div>
                      );
                    })}
                    {locations.length === 0 && (
                      <p className="p-4 text-sm text-muted-foreground text-center">
                        No locations available
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* Permissions Tab */}
              {activeTab === 'permissions' && (
                <PermissionEditor
                  userId={staff.id}
                  userRole={staff.role}
                  disabled={!canEdit()}
                />
              )}
            </div>
          </ResponsiveModalBody>

          <ResponsiveModalFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="blaze"
              disabled={updateStaff.isPending || !canEdit()}
            >
              {updateStaff.isPending && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              Save Changes
            </Button>
          </ResponsiveModalFooter>
        </form>

        {/* Location Permission Editor Dialog */}
        {selectedLocationForPerms && (
          <LocationPermissionEditor
            open={locationPermEditorOpen}
            onOpenChange={(open) => {
              setLocationPermEditorOpen(open);
              if (!open) {
                // Reload override counts when dialog closes
                if (staff) {
                  loadLocationOverrideCounts(staff.id);
                }
                setSelectedLocationForPerms(null);
              }
            }}
            userId={staff.id}
            userRole={staff.role}
            locationId={selectedLocationForPerms.id}
            locationName={selectedLocationForPerms.name}
          />
        )}
      </ResponsiveModalContent>
    </ResponsiveModal>
  );
}
